import { getCategoryLabel, getVerificationDocumentStatusDisplay, VerificationDocumentLockButton } from '@app/components'
import { useUploadedDocuments } from '@app/lib'
import { Alert, Badge, Box, Button, createStyles, LoadingOverlay, Stack, Table, Text } from '@mantine/core'
import { IconAlertCircle } from '@tabler/icons-react'
import dayjs from 'dayjs'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import { map, truncate } from 'lodash'
import { type FC } from 'react'

dayjs.extend(localizedFormat)

const useStyles = createStyles(() => ({
  fitWidth: {
    whiteSpace: 'nowrap',
    width: 1
  },
  lockColumn: {
    textAlign: 'center',
    verticalAlign: 'middle',
    whiteSpace: 'nowrap',
    width: 1
  }
}))

export interface VerificationDocumentsTableProps {
  enrollmentId: string
  programYearId: string
}

export const VerificationDocumentsTable: FC<VerificationDocumentsTableProps> = ({ enrollmentId, programYearId }) => {
  const { classes } = useStyles()
  const { data, isError, isFetching, refetch } = useUploadedDocuments({ enrollmentId, programYearId })

  return (
    <Stack
      pos='relative'
      spacing='lg'
    >
      <LoadingOverlay visible={isFetching} />
      {isError ? (
        <Alert
          color='red'
          icon={<IconAlertCircle size={16} />}
          title='Could not load documents'
        >
          <Stack spacing='sm'>
            <Text size='sm'>Something went wrong loading the uploaded documents. Please try again.</Text>
            <Box>
              <Button
                color='red'
                onClick={() => refetch()}
                size='xs'
                variant='light'
              >
                Retry
              </Button>
            </Box>
          </Stack>
        </Alert>
      ) : (
        <Table
          horizontalSpacing='xs'
          verticalSpacing='xs'
          withBorder
        >
          <thead>
            <tr>
              <th>
                <Text
                  color='black'
                  fw='600'
                >
                  Filename
                </Text>
              </th>
              <th className={classes.fitWidth}>
                <Text
                  color='black'
                  fw='600'
                >
                  Category
                </Text>
              </th>
              <th className={classes.fitWidth}>
                <Text
                  color='black'
                  fw='600'
                >
                  Date Uploaded
                </Text>
              </th>
              <th className={classes.fitWidth}>
                <Text
                  color='black'
                  fw='600'
                >
                  Status
                </Text>
              </th>
              <th
                aria-label='Access'
                className={classes.lockColumn}
              />
            </tr>
          </thead>
          <tbody>
            {data?.data.length === 0 && (
              <tr>
                <td colSpan={5}>
                  <Text
                    align='center'
                    color='dimmed'
                    py='md'
                  >
                    No verification documents uploaded for this program year yet.
                  </Text>
                </td>
              </tr>
            )}
            {map(data?.data, (doc) => {
              const statusDisplay = getVerificationDocumentStatusDisplay(doc.status)

              return (
                // No data-document-id attribute by design — defense-in-depth so the DOM
                // does not enable URL construction for the deny-listed download endpoint.
                <tr key={doc.id}>
                  <td>{truncate(doc.filename, { length: 50 })}</td>
                  <td className={classes.fitWidth}>{getCategoryLabel(doc.category)}</td>
                  <td className={classes.fitWidth}>{doc.dateUploaded ? dayjs(doc.dateUploaded).format('LL') : '—'}</td>
                  <td className={classes.fitWidth}>
                    {statusDisplay ? (
                      <Badge
                        color={statusDisplay.color}
                        variant='filled'
                      >
                        {statusDisplay.label}
                      </Badge>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className={classes.lockColumn}>
                    <VerificationDocumentLockButton documentTypeLabel={getCategoryLabel(doc.category)} />
                  </td>
                </tr>
              )
            })}
          </tbody>
        </Table>
      )}
    </Stack>
  )
}
